/**
 * La receta de una atención: emitirla y verla.
 *
 * Se usa en dos sitios con el mismo código: dentro del formulario de atender
 * —que es donde el médico está cuando decide qué recetar— y en el episodio ya
 * firmado, para consultarla después. Lo único que necesita es que el episodio
 * exista, aunque sea como borrador.
 *
 * Lo que se receta queda copiado como texto: si mañana renombran el producto,
 * el papel que firmó el médico no cambia.
 *
 * El buscador ofrece el catálogo de la farmacia con su stock, pero deja
 * escribir cualquier cosa: se receta lo que el paciente necesita, esté o no en
 * el anaquel. Recetar por inventario es atender al revés.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  Pencil,
  Pill,
  Plus,
  Printer,
  Trash2,
  Upload,
} from "lucide-react";
import {
  type ProductoParaRecetar,
  type Receta,
  type RecetaItemIn,
  buscarParaRecetar,
  type CorteReceta,
  abrirRecetaPdf,
  crearReceta,
  editarReceta,
  eliminarReceta,
  listRecetas,
  subirRecetaFirmada,
} from "../../api/recetas";
import { formatApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { useZonaHoraria } from "../../auth/TenantBrandingProvider";
import { fechaHora } from "../../lib/fecha";

import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Dialog,
  Field,
  Input,
} from "../ui";

interface LineaReceta extends RecetaItemIn {
  nombre: string;
  stock?: number | null;
}

export function RecetaEpisodio({
  episodioId,
  cerrada = false,
  permitirAgregar = false,
}: {
  episodioId: number;
  /** La atención está firmada: no nace nada nuevo salvo que se pida aparte. */
  cerrada?: boolean;
  /** El médico pulsó "Añadir a esta atención" y sabe lo que hace. */
  permitirAgregar?: boolean;
}) {
  const { me } = useAuth();
  const tz = useZonaHoraria();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [corrigiendo, setCorrigiendo] = useState<Receta | null>(null);

  const [borrando, setBorrando] = useState<Receta | null>(null);
  const bloqueado = cerrada && !permitirAgregar;

  const borrar = useMutation({
    mutationFn: (id: number) => eliminarReceta(id),
    onSuccess: () => {
      setBorrando(null);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["recetas"] });
    },
    onError: (e) => {
      setBorrando(null);
      setError(formatApiError(e, "No se pudo borrar la receta."));
    },
  });

  const puedeRecetar = me?.permisos.includes("receta:create") ?? false;
  const puedeVer = me?.permisos.includes("receta:read") ?? false;

  const { data: recetas = [] } = useQuery({
    queryKey: ["recetas", "episodio", episodioId],
    queryFn: () => listRecetas({ episodio_id: episodioId }),
    enabled: puedeVer,
  });


  if (!puedeVer && !puedeRecetar) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-slate-900">
              <Pill className="h-4 w-4 text-[color:var(--color-link)]" aria-hidden="true" />
              Receta
              {recetas.length > 0 && <Badge variant="neutral">{recetas.length}</Badge>}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Lo que el paciente retira en farmacia.
            </p>
          </div>
          {/* Una receta por atención.
 
              Con una ya emitida, el botón invitaba a hacer OTRA — y así
              acababan tres recetas del mismo tratamiento en la misma consulta,
              que es justo lo que se quitó. Si algo está mal, se edita.

              Vuelve a aparecer cuando la que hay ya no se puede tocar: farmacia
              despachó o está firmada a mano. Ahí sí hace falta emitir otra,
              porque editar no es una opción y el paciente puede necesitar algo
              más. */}
          {/* `bloqueado` sale de la atención firmada, no del permiso: el
              médico sigue teniendo permiso de recetar, lo que no puede es
              colgarle algo nuevo a lo que ya firmó sin decirlo. */}
          {puedeRecetar && !bloqueado && recetas.every((r) => !r.editable) && (
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" />
              {recetas.length === 0 ? "Emitir receta" : "Emitir otra receta"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {error && <Alert>{error}</Alert>}
        {recetas.length === 0 && (
          <p className="text-sm text-slate-400">
            Sin receta. No toda atención termina en una.
          </p>
        )}

        {recetas.map((r) => (
          <div key={r.id} className="rounded-lg border border-slate-100 p-3">
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-slate-900">Receta #{r.id}</span>
              <Badge
                variant={
                  r.estado === "DISPENSADA"
                    ? "success"
                    : r.estado === "ANULADA"
                      ? "danger"
                      : r.estado === "PARCIAL"
                        ? "warning"
                        : "neutral"
                }
              >
                {r.estado === "PENDIENTE"
                  ? "Pendiente en farmacia"
                  : r.estado === "PARCIAL"
                    ? "Entregada en parte"
                    : r.estado === "DISPENSADA"
                      ? "Entregada"
                      : r.estado === "NO_RETIRADA"
                        ? "No retirada"
                        : "Anulada"}
              </Badge>
              <span className="text-xs text-slate-500">{fechaHora(r.fecha, tz)}</span>
              {/* Que no se lea como parte de la consulta algo que se añadió
                  otro día. */}
              {r.posterior_al_cierre && (
                <Badge variant="warning">Añadida tras firmar</Badge>
              )}
              {/* El paciente sale con el papel en la mano: hay farmacias que
                  no despachan sin receta impresa y firmada.

                  Una ANULADA no se imprime jamás. Estaba ofreciendo el botón
                  igual, y ese papel sale del consultorio sin nada que diga que
                  no vale: el paciente lo lleva a la farmacia y pide un
                  medicamento que el médico retiró. */}
              {r.estado !== "ANULADA" && (
                <span className="ml-auto">
                  <MenuImprimir receta={r} />
                </span>
              )}
              {/* Solo mientras farmacia no la haya tocado. Despachada ya salió
                  medicamento por la ventanilla: eso se corrige con una receta
                  nueva, no borrando la anterior. El backend responde 409. */}
              {/* Editar va antes que anular porque es lo que se hace casi
                  siempre: te equivocaste en un renglón, no quieres retirar la
                  receta entera. Anular queda para lo otro —la recetaste al
                  paciente que no era—, y entonces sí se emite otra desde cero. */}
              {puedeRecetar && r.editable && !bloqueado && (
                <>
                  <button
                    type="button"
                    onClick={() => setCorrigiendo(r)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => setBorrando(r)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Borrar
                  </button>
                </>
              )}
            </p>

            {/* El reparto. El paciente pregunta "¿esto lo tienen aquí?" antes
                de salir del consultorio, y el médico debe poder responderlo
                sin llamar al mostrador. */}
            {r.total_fuera > 0 && (
              <p className="mt-1 text-xs text-slate-600">
                <span className="font-medium text-emerald-700">
                  {r.total_en_farmacia} en la farmacia
                </span>
                {" · "}
                <span className="font-medium text-amber-700">
                  {r.total_fuera} lo compra fuera
                </span>
              </p>
            )}

            {/* En columnas, no en texto corrido.

                Los medicamentos tienen nombres de largo muy distinto
                —"acido folico" frente a "Dexametasona 4 mg/2 mL Ampolla"— y
                escritos seguidos, la dosis de uno cae donde el nombre del
                siguiente. Para comparar dos renglones había que leerlos
                enteros. Con columnas, la vista baja por la que le interesa.

                La tabla scrollea sola en pantalla angosta: lo que no puede
                pasar es que la página entera se mueva de lado. */}
            <div className="mt-2 overflow-x-auto">
              <table className="w-full min-w-[34rem] text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="py-1 pr-3 font-medium">Medicamento</th>
                    <th className="py-1 pr-3 font-medium">Dosis</th>
                    <th className="py-1 pr-3 font-medium">Cada</th>
                    <th className="py-1 pr-3 font-medium">Por</th>
                    <th className="py-1 text-right font-medium">Cant.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {r.items.map((i) => (
                    <tr key={i.id} className="align-top">
                      <td className="py-1.5 pr-3 text-slate-800">
                        {i.descripcion}
                        {/* En las recetas viejas no se anotaba el saldo: sin
                            ese dato no se afirma que faltara, se calla. */}
                        {!i.en_farmacia &&
                          (i.producto_id == null || i.stock_al_recetar != null) && (
                            <span className="ml-1.5 whitespace-nowrap rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                              {i.producto_id == null ? "no se vende aquí" : "sin stock"}
                            </span>
                          )}
                        {/* La nota de ESTE medicamento, debajo de su nombre y
                            no en una columna: es texto libre y de largo
                            impredecible, y metida en la rejilla desalinearía
                            todo lo demás. */}
                        {i.indicacion && (
                          <span className="block text-xs italic text-slate-500">
                            {i.indicacion}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3 text-slate-600">{i.dosis ?? "—"}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{i.frecuencia ?? "—"}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{i.duracion ?? "—"}</td>
                      <td className="py-1.5 text-right font-mono text-slate-700">
                        {i.cantidad}
                        {i.cantidad_dispensada > 0 && (
                          <span className="block text-[11px] text-emerald-700">
                            {i.cantidad_dispensada} entregado
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {r.indicaciones && (
              <p className="mt-1 text-xs italic text-slate-500">{r.indicaciones}</p>
            )}
            {/* El mismo campo lo escriben dos manos distintas: farmacia al
                cerrarla sin retirar, y el médico al anularla. Decir siempre
                "cerrada en farmacia" señalaba a farmacia por una decisión del
                médico. */}
            {r.motivo_cierre && (
              <p className="mt-1 text-xs text-slate-500">
                {r.estado === "ANULADA" ? "Anulada" : "Cerrada en farmacia"}:{" "}
                {r.motivo_cierre}
              </p>
            )}

            {puedeRecetar && r.estado !== "ANULADA" && (
              <SubirFirma receta={r} onError={setError} />
            )}
          </div>
        ))}
      </CardBody>

      <ConfirmDialog
        open={borrando != null}
        onClose={() => setBorrando(null)}
        onConfirm={() => borrando && borrar.mutate(borrando.id)}
        loading={borrar.isPending}
        title="Borrar esta receta"
        description="Desaparece de la ficha. Si solo te equivocaste en un medicamento, ciérrala y usa Editar."
        detalle={
          borrando && (
            <>
              Receta #{borrando.id} ·{" "}
              <span className="font-medium">
                {borrando.items.length} medicamento
                {borrando.items.length === 1 ? "" : "s"}
              </span>
            </>
          )
        }
        confirmLabel="Sí, borrarla"
        cancelLabel="No, volver"
      />

      {(open || corrigiendo) && (
        <EmitirRecetaDialog
          episodioId={episodioId}
          cerrada={cerrada}
          corrige={corrigiendo}
          onClose={() => {
            setOpen(false);
            setCorrigiendo(null);
          }}
          onSaved={() => {
            setOpen(false);
            setCorrigiendo(null);
            setError(null);
            queryClient.invalidateQueries({ queryKey: ["recetas"] });
          }}
          onError={setError}
        />
      )}
    </Card>
  );
}

/**
 * Subir la receta firmada a mano.
 *
 * El circuito real es: imprimir, firmar, sellar, fotografiar con el móvil y
 * subir. A partir de ahí el paciente se la descarga del portal y no vuelve al
 * consultorio a por el papel — que es lo que pasa hoy cuando la farmacia se la
 * rechaza por no tener firma.
 */
/**
 * Las formas de sacar el papel.
 *
 * EL BOTÓN NORMAL IMPRIME LA COMPLETA. Lo demás está detrás, porque la mayoría
 * de las veces no hace falta: si todo está en la farmacia del policlínico, o
 * nada lo está, partir la receta no significa nada y las tres opciones serían
 * la misma tres veces.
 *
 * Los cortes aparecen SOLO cuando hay mezcla. "Sin firma" aparece siempre: hay
 * farmacias que exigen la rúbrica de puño sobre el papel y no aceptan una
 * estampada, y ese papel lo descarga el médico para firmarlo.
 */
function MenuImprimir({ receta }: { receta: Receta }) {
  const [abierto, setAbierto] = useState(false);
  const hayMezcla = receta.total_en_farmacia > 0 && receta.total_fuera > 0;

  async function imprimir(opciones: { corte?: CorteReceta; firma?: boolean }) {
    setAbierto(false);
    await abrirRecetaPdf(receta.id, opciones);
  }

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => imprimir({})}
        className="inline-flex items-center gap-1.5 rounded-l-lg px-2.5 py-1 text-xs font-medium text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
      >
        <Printer className="h-3.5 w-3.5" />
        Imprimir
      </button>
      <button
        type="button"
        onClick={() => setAbierto(!abierto)}
        aria-label="Otras formas de imprimir"
        className="inline-flex items-center rounded-r-lg border-l border-slate-200 px-1.5 py-1 text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>

      {abierto && (
        <>
          {/* Capa para cerrar tocando fuera. Sin ella el menú se queda abierto
              detrás de lo siguiente que haga el médico. */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setAbierto(false)}
          />
          <div className="absolute right-0 top-full z-20 mt-1 w-64 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            {hayMezcla && (
              <>
                <p className="px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Partida
                </p>
                <OpcionMenu
                  onClick={() => imprimir({ corte: "policlinico" })}
                  titulo="Solo la del policlínico"
                  detalle={`${receta.total_en_farmacia} que puede retirar aquí`}
                />
                <OpcionMenu
                  onClick={() => imprimir({ corte: "fuera" })}
                  titulo="Solo lo de fuera"
                  detalle={`${receta.total_fuera} que compra en otra farmacia`}
                />
                <div className="my-1 border-t border-slate-100" />
              </>
            )}
            <OpcionMenu
              onClick={() => imprimir({ firma: false })}
              titulo="Sin firma"
              detalle="Para firmarla de tu puño"
            />
          </div>
        </>
      )}
    </span>
  );
}

function OpcionMenu({
  onClick,
  titulo,
  detalle,
}: {
  onClick: () => void;
  titulo: string;
  detalle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full px-3 py-2 text-left hover:bg-slate-50"
    >
      <span className="block text-sm text-slate-800">{titulo}</span>
      <span className="block text-xs text-slate-500">{detalle}</span>
    </button>
  );
}

function SubirFirma({
  receta,
  onError,
}: {
  receta: Receta;
  onError: (m: string) => void;
}) {
  const queryClient = useQueryClient();
  const [arrastrando, setArrastrando] = useState(false);

  const subir = useMutation({
    mutationFn: (f: File) => subirRecetaFirmada(receta.id, f),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["recetas"] }),
    onError: (e) => onError(formatApiError(e, "No se pudo subir la receta firmada.")),
  });

  if (receta.tiene_firma) {
    return (
      <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
        <Check className="h-3.5 w-3.5" />
        Receta firmada subida — el paciente puede descargarla
      </p>
    );
  }

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setArrastrando(true);
      }}
      onDragLeave={() => setArrastrando(false)}
      onDrop={(e) => {
        e.preventDefault();
        setArrastrando(false);
        const f = e.dataTransfer.files?.[0];
        if (f) subir.mutate(f);
      }}
      className={`mt-2 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs transition ${
        arrastrando
          ? "border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
          : "border-slate-300 text-slate-500 hover:border-slate-400"
      }`}
    >
      <Upload className="h-3.5 w-3.5" />
      {subir.isPending
        ? "Subiendo…"
        : "Arrastra aquí la receta firmada, o haz clic para elegirla"}
      <input
        type="file"
        accept="image/png,image/jpeg,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) subir.mutate(f);
        }}
      />
    </label>
  );
}

function EmitirRecetaDialog({
  episodioId,
  cerrada = false,
  corrige,
  onClose,
  onSaved,
  onError,
}: {
  episodioId: number;
  /** Se emite sobre una atención ya firmada: hay que confirmarlo. */
  cerrada?: boolean;
  /**
   * La receta que se está corrigiendo, si se llegó por ahí.
   *
   * Corregir NO edita: emite una receta nueva y retira la anterior. Lo que
   * cambia es de dónde parte el formulario — con los medicamentos ya puestos.
   * Sin esto, equivocarse en uno de quince costaba teclear los quince otra
   * vez, y nadie corrige a ese precio: se quedan las recetas mal.
   */
  corrige?: Receta | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [q, setQ] = useState("");
  const [lineas, setLineas] = useState<LineaReceta[]>(() =>
    (corrige?.items ?? []).map((i) => ({
      producto_id: i.producto_id,
      nombre: i.descripcion,
      descripcion: i.descripcion,
      dosis: i.dosis ?? "",
      frecuencia: i.frecuencia ?? "",
      duracion: i.duracion ?? "",
      indicacion: i.indicacion ?? "",
      cantidad: i.cantidad,
      stock: i.stock_disponible,
    })),
  );
  const [indicaciones, setIndicaciones] = useState(corrige?.indicaciones ?? "");
  /** Filas con la nota abierta sin texto todavía. Con texto se abre sola. */
  const [abiertas, setAbiertas] = useState<Set<number>>(new Set());

  // Catálogo servido por el propio recetario: el médico ve nombre,
  // concentración y stock, y nada de precios, costos ni lotes. Eso es del
  // mostrador, no de quien receta.
  const { data: encontrados = [] } = useQuery({
    queryKey: ["recetar", "buscar", q],
    queryFn: () => buscarParaRecetar(q),
    enabled: q.trim().length >= 2,
  });

  const disponibles = encontrados.filter(
    (p) => !lineas.some((l) => l.producto_id === p.id),
  );

  // El reparto se calcula con el stock que el buscador acaba de traer: se
  // cubre lo que hay entero, no lo que hay a medias. Media caja no le sirve al
  // paciente, tiene que ir a comprarla igual.
  const enFarmacia = lineas.filter(
    (l) => l.producto_id != null && (l.stock ?? 0) >= l.cantidad,
  );
  const fuera = lineas.filter(
    (l) => l.producto_id == null || (l.stock ?? 0) < l.cantidad,
  );

  const emitir = useMutation({
    mutationFn: async () => {
      // Editar reescribe la MISMA receta. No se emite otra ni queda una
      // anulada: una versión tachada de algo que estuvo mal escrito un minuto
      // solo estorba a quien lea la historia dentro de un año.
      if (corrige) {
        return editarReceta(corrige.id, {
          indicaciones: indicaciones.trim() || null,
          items: lineas.map((l) => ({
            producto_id: l.producto_id ?? null,
            descripcion: l.descripcion ?? l.nombre,
            dosis: l.dosis || null,
            frecuencia: l.frecuencia || null,
            duracion: l.duracion || null,
            indicacion: l.indicacion || null,
            cantidad: l.cantidad,
          })),
        });
      }
      const nueva = await crearReceta({
        episodio_id: episodioId,
        confirmar_posterior: cerrada,
        indicaciones: indicaciones.trim() || null,
        items: lineas.map((l) => ({
          producto_id: l.producto_id ?? null,
          descripcion: l.descripcion ?? l.nombre,
          dosis: l.dosis || null,
          frecuencia: l.frecuencia || null,
          duracion: l.duracion || null,
          indicacion: l.indicacion || null,
          cantidad: l.cantidad,
        })),
      });
      return nueva;
    },
    onSuccess: onSaved,
    onError: (e) =>
      onError(
        formatApiError(
          e,
          corrige ? "No se pudo corregir la receta." : "No se pudo emitir la receta.",
        ),
      ),
  });

  /** Sube la cantidad de una línea ya puesta. Devuelve si la encontró. */
  function sumarSiYaEsta(indice: number): boolean {
    if (indice < 0) return false;
    setLineas(
      lineas.map((l, i) => (i === indice ? { ...l, cantidad: l.cantidad + 1 } : l)),
    );
    return true;
  }

  function agregarProducto(p: ProductoParaRecetar) {
    setQ("");
    // El mismo producto dos veces en la misma receta no es una indicación, es
    // un error de tecleo: en el papel saldrían dos renglones idénticos y la
    // farmacia no sabría si son dos cosas o una repetida. Otra presentación
    // —500 mL frente a 1 L— es OTRO producto y sí puede convivir.
    if (sumarSiYaEsta(lineas.findIndex((l) => l.producto_id === p.id))) return;
    setLineas([
      ...lineas,
      {
        producto_id: p.id,
        nombre: [p.nombre, p.concentracion, p.forma_farmaceutica]
          .filter(Boolean)
          .join(" "),
        descripcion: [p.nombre, p.concentracion, p.forma_farmaceutica]
          .filter(Boolean)
          .join(" "),
        cantidad: 1,
        dosis: "",
        frecuencia: "",
        duracion: "",
        stock: p.stock,
      },
    ]);
  }

  function agregarLibre(desdeElBuscador: string) {
    const texto = desdeElBuscador.trim();
    if (texto.length < 3) return;
    // Lo escrito a mano no tiene id, así que se compara por el texto.
    if (
      sumarSiYaEsta(
        lineas.findIndex(
          (l) =>
            l.producto_id == null &&
            (l.descripcion ?? l.nombre).trim().toLowerCase() === texto.toLowerCase(),
        ),
      )
    ) {
      setQ("");
      return;
    }
    setLineas([
      ...lineas,
      {
        producto_id: null,
        nombre: texto,
        descripcion: texto,
        cantidad: 1,
        dosis: "",
        frecuencia: "",
        duracion: "",
        stock: null,
      },
    ]);
    // Se limpia el buscador: lo que se buscaba ya está en la receta.
    setQ("");
  }

  function set(idx: number, campo: keyof LineaReceta, valor: unknown) {
    setLineas(lineas.map((l, i) => (i === idx ? { ...l, [campo]: valor } : l)));
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={corrige ? `Editar la receta #${corrige.id}` : "Emitir receta"}
      description={
        corrige
          ? "Sigue siendo la misma receta. Si ya la imprimiste, vuelve a imprimirla y dale al paciente el papel corregido."
          : "Queda pendiente en farmacia hasta que el paciente la retire."
      }
      size="lg"
    >
      <div className="space-y-4">
        <div className="relative">
          <Field
            label="Buscar en la farmacia"
            htmlFor="r_buscar"
            hint="Por nombre comercial o principio activo. Muestra el stock real."
          >
            <Input
              id="r_buscar"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Paracetamol, amoxicilina…"
            />
          </Field>
          {/* Lo ya puesto sale de la lista: ofrecerlo otra vez es invitar a
              duplicarlo. Si hace falta más, se sube la cantidad en su línea. */}
          {q.trim().length >= 2 && disponibles.length === 0 && encontrados.length > 0 && (
            <p className="mt-1 text-xs text-slate-500">
              Ya está en la receta. Ajusta la cantidad en su línea, abajo.
            </p>
          )}

          {/* Lo que la farmacia no tiene se receta desde AQUÍ.

              Había un segundo campo, "O escribir libremente", debajo. El
              médico escribía "acido folico" en el buscador, no salía nada, y
              tenía que volver a teclearlo entero en el otro campo. Dos veces
              lo mismo por un dato que ya había escrito.

              Que la farmacia no lo tenga no cambia lo que el paciente
              necesita: se receta igual y lo compra fuera. */}
          {q.trim().length >= 3 && encontrados.length === 0 && (
            <button
              type="button"
              onClick={() => agregarLibre(q)}
              className="mt-2 flex w-full items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-left text-sm hover:border-[color:var(--color-link)] hover:bg-slate-50"
            >
              <Plus className="h-4 w-4 text-[color:var(--color-link)]" />
              <span>
                <span className="font-medium text-slate-900">
                  Recetar «{q.trim()}» igualmente
                </span>
                <span className="block text-xs text-slate-500">
                  No está en la farmacia del policlínico. El paciente lo compra fuera.
                </span>
              </span>
            </button>
          )}
          {q.trim().length >= 2 && disponibles.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
              {disponibles.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => agregarProducto(p)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                  >
                    <span>
                      {p.nombre} {p.concentracion ?? ""}
                      <span className="ml-2 text-xs text-slate-400">
                        {p.principio_activo ?? ""}
                      </span>
                    </span>
                    <span
                      className={`font-mono text-xs ${
                        p.stock > 0 ? "text-slate-500" : "text-red-600"
                      }`}
                    >
                      stock {p.stock}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {lineas.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">
            Agrega al menos un medicamento.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {lineas.map((l, idx) => (
              <li key={idx} className="space-y-2 py-3">
                <div className="flex items-center gap-2">
                  <span className="grow text-sm font-medium text-slate-900">
                    {l.nombre}
                    {l.stock != null && (
                      <span
                        className={`ml-2 font-mono text-xs ${
                          l.stock > 0 ? "text-slate-400" : "text-red-600"
                        }`}
                      >
                        stock {l.stock}
                      </span>
                    )}
                    {l.producto_id == null && (
                      <span className="ml-2 text-xs text-amber-700">fuera de catálogo</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => setLineas(lineas.filter((_, i) => i !== idx))}
                    className="text-slate-400 hover:text-red-600"
                    aria-label="Quitar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Input
                    value={l.dosis ?? ""}
                    onChange={(e) => set(idx, "dosis", e.target.value)}
                    placeholder="1 tableta"
                  />
                  <Input
                    value={l.frecuencia ?? ""}
                    onChange={(e) => set(idx, "frecuencia", e.target.value)}
                    placeholder="cada 8 horas"
                  />
                  <Input
                    value={l.duracion ?? ""}
                    onChange={(e) => set(idx, "duracion", e.target.value)}
                    placeholder="por 3 días"
                  />
                  <Input
                    type="number"
                    min={1}
                    value={String(l.cantidad)}
                    onChange={(e) =>
                      set(idx, "cantidad", Math.max(1, Number(e.target.value) || 1))
                    }
                    className="font-mono"
                    placeholder="Cantidad"
                  />
                </div>

                {/* La nota de ESTE medicamento, escondida hasta que hace falta.

                    "Tomar con alimentos" es de un medicamento, no de la receta:
                    puesta en las indicaciones generales se la come también el
                    que va en ayunas. Pero la mayoría de renglones no lleva
                    ninguna, así que un campo siempre abierto por medicamento
                    convierte una receta de quince en un muro. Se abre con el
                    "+" y, si ya trae texto, se queda abierto solo. */}
                {abiertas.has(idx) || (l.indicacion ?? "") !== "" ? (
                  <Input
                    value={l.indicacion ?? ""}
                    onChange={(e) => set(idx, "indicacion", e.target.value)}
                    placeholder="Con alimentos. No manejar después de tomarlo."
                    autoFocus={abiertas.has(idx) && !l.indicacion}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setAbiertas(new Set(abiertas).add(idx))}
                    className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium text-[color:var(--color-link)] hover:underline"
                  >
                    <Plus className="h-3 w-3" />
                    Indicación para este medicamento
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Qué se lleva de aquí y qué tiene que ir a buscar. Se dice ANTES de
            emitir porque es lo que el médico le explica al paciente mientras
            todavía lo tiene sentado enfrente. */}
        {lineas.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            {fuera.length === 0 ? (
              <span className="text-emerald-700">
                Todo está en la farmacia del policlínico.
              </span>
            ) : enFarmacia.length === 0 ? (
              <span className="text-amber-800">
                Nada de esto lo tenemos: el paciente lo comprará fuera. Farmacia
                se entera igual, para saber qué le están pidiendo.
              </span>
            ) : (
              <span className="text-slate-700">
                <span className="font-medium text-emerald-700">
                  {enFarmacia.length} en la farmacia
                </span>{" "}
                y{" "}
                <span className="font-medium text-amber-800">
                  {fuera.length} que el paciente compra fuera
                </span>
                . La receta se emite completa; el reparto es solo para saber a
                qué atenerse.
              </span>
            )}
          </div>
        )}

        <Field label="Indicaciones para toda la receta" htmlFor="r_ind">
          <textarea
            id="r_ind"
            value={indicaciones}
            onChange={(e) => setIndicaciones(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Tomar con alimentos. Volver si la fiebre persiste 48 h."
          />
        </Field>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => emitir.mutate()}
            disabled={lineas.length === 0 || emitir.isPending}
            loading={emitir.isPending}
          >
            {corrige ? "Guardar cambios" : "Emitir receta"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
